import { useState } from "react"
import { useParams } from "react-router-dom"
import { useUpdateContactStateMutation } from "../../../../../../features/contactsApi"
import { useRemovePersonMutation } from "../../../../../../features/peopleApi"
import { addBannerWithTimeout } from "../../../../../lib/centralizedBanner/slice"
import { ContactDTO, ContactType } from "../../../../../lib/filters/dto"
import { keycloak } from "../../../../../lib/keycloak"
import { useAppDispatch } from "../../../../../lib/redux/hooks"
import { colors } from "../../../../../ui/colors"
import { CheckCircleIcon } from "../../../../../ui/icons/checkCircle"
import { ChevronIcon } from "../../../../../ui/icons/chevron"
import { MinusCircleIcon } from "../../../../../ui/icons/minusCircle"
import { TrashIcon } from "../../../../../ui/icons/trash"
import Button from "../../../../../ui/molecules/button"
import { GroupSelectCustom } from "../../../../../ui/molecules/groupSelect"
import { ConfirmModal } from "../../../../../ui/organisms/confirmModal.tsx"
import { LeadTakenModal } from "../../../../targetList/contacts/leadTakenModal"
import { ContactStateEnum, generateDefaultValue, generateOutcomeOptions } from "../../../../targetList/dto"
import { PersonDTO } from "../../../people/dto"
import { AssociateCompanyModal } from "../../../people/editPerson/associateCompany"
import { EditPersonForm } from "../../../people/editPerson/form"
import { DissociateEmployeeModal } from "./dissociateEmployeeModal"
import { NotSavedChangesModal } from "./notSavedChanges"

interface Props {
    isInTargetList: boolean
    employee?: PersonDTO
    navigate: (section: 'list' | 'new' | 'edit') => void
    contact: ContactDTO<ContactType.PERSON> | undefined
}

export function EditEmployee(props: Props) {
    const dispatch = useAppDispatch()
    const { targetId, targetContactId } = useParams()

    const [openDissociateEmployeeModal, setOpenDissociateEmployeeModal] = useState(false)
    const [openAssociateModal, setOpenAssociateModal] = useState(false)
    const [deleteEmployeeModalOpen, setDeleteEmployeeModalOpen] = useState(false)
    const [isEdited, setIsEdited] = useState(false)
    const [confirmLeadTaken, setConfirmLeadTaken] = useState(false)
    const [showNotSavedChanges, setShowNotSavedChanges] = useState(false)
    const [deleteEmployee, { isLoading: isDeletingEmployee }] = useRemovePersonMutation()
    const [updateContactState, { isLoading: isUpdatingContactState }] = useUpdateContactStateMutation();

    const handleUpdateContactState = async (outcome: ContactStateEnum, outcomeInfo: string | null) => {
        try {
            await updateContactState({
                contactId: props.contact?.id!,
                outcome,
                outcomeInfo,
                contactType: ContactType.PERSON,
                targetId: targetId!,
                companyId: targetContactId!
            }).unwrap();
            dispatch(addBannerWithTimeout({
                message: "Stato contatto aggiornato con successo",
                variant: "success"
            }));
            setConfirmLeadTaken(false);
        } catch (error) {
            const res = error as unknown as { status: number, data: { errors: { message: string }[] } };
            let message = "Si è verificato un errore durante l'aggiornamento dello stato del contatto.";
            const errors = res?.data?.errors ?? [];
            if (errors?.some(e => e.message.includes("has already reached the maximum number of calls"))) {
                message = "Il contatto ha già raggiunto il numero massimo di chiamate consentite.";
            } else if (errors?.some(e => e.message.includes("Contact ID is required"))) {
                message = "Non è possibile prendere il Lead perché non ancora presente in Lista Target. Riprova domani.";
            } else if (errors?.some(e => e.message.includes("Invalid transition from DRAFT to DRAFT"))) {
                message = "Il contatto è già nello stato 'Da Lavorare'";
            }
            dispatch(addBannerWithTimeout({
                variant: "error",
                message: message,
            }));
        }
    }

    const handleDeleteEmployee = async () => {
        try {
            await deleteEmployee({ ids: [props.employee?.id!], companyId: props.employee?.companyId! }).unwrap();
            dispatch(addBannerWithTimeout({
                message: "Dipendente eliminato con successo",
                variant: "success"
            }));
            props.navigate('list');
            setDeleteEmployeeModalOpen(false);
        } catch {
            dispatch(addBannerWithTimeout({
                message: "Errore durante l'eliminazione del dipendente",
                variant: "error"
            }));
        }
    }

    return (
        <div className="px-4">
            <div className="border-y border-neutral-200 py-3 flex items-center justify-between">
                <div className="flex items-center gap-1 cursor-pointer" onClick={() => {
                    if (
                        isEdited &&
                        !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')))
                        setShowNotSavedChanges(true)
                    else
                        props.navigate('list')
                }}>
                    <ChevronIcon color={colors.neutral[400]} size={24} direction="l" />
                    <span className="text-text-sm font-semibold text-neutral-700">Torna alla lista</span>
                </div>
            </div>
            {
                props.isInTargetList &&
                <div className="px-6 pt-6 flex items-end gap-2">
                    <GroupSelectCustom
                        disabled={props.employee?.callsCounter! >= 3 || keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')}
                        menuPortalTargetIsNotBody
                        placeholder={"Seleziona un esito"}
                        label="Esito"
                        onChange={(e) => {
                            if (e === ContactStateEnum.DRAFT || e === ContactStateEnum.LEADTAKEN) {
                                handleUpdateContactState(e, null);
                                // dispatch(
                                //     changeContactState({
                                //         contactState: e,
                                //         contactId: props.contact?.id!,
                                //         stateInfo: null,
                                //         index: -1
                                //     })
                                // );
                            } else {
                                handleUpdateContactState(e && e !== undefined && typeof e === 'string' ? e.split('::')[0] as ContactStateEnum : ContactStateEnum.DRAFT, e && e !== undefined && typeof e === 'string' ? e.split('::')[1] : null);
                                // dispatch(
                                //     changeContactState({
                                //         contactState: e && e !== undefined && typeof e === 'string' ? e.split('::')[0] as ContactStateEnum : ContactStateEnum.DRAFT,
                                //         contactId: props.contact?.id!,
                                //         stateInfo: e && e !== undefined && typeof e === 'string' ? e.split('::')[1] : null,
                                //         index: -1
                                //     })
                                // );
                            }
                        }}
                        defaultValue={generateDefaultValue(props.contact?.outcome || ContactStateEnum.DRAFT, props.contact?.outcomeInfo ?? null)}
                        options={generateOutcomeOptions(props.contact?.callAttempts!, props.contact?.noResponseCount!, props.contact?.outcome || ContactStateEnum.DRAFT, ContactType.PERSON)}
                    />
                    <div>
                        <Button
                            size={"md"}
                            disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator') || props.contact?.outcome === ContactStateEnum.LEADTAKEN || (props.employee?.callsCounter! >= 3)}
                            iconPosition={props.contact?.outcome === ContactStateEnum.LEADTAKEN ? "left" : 'off'}
                            variant={props.contact?.outcome === ContactStateEnum.LEADTAKEN ? "solid" : "outline"}
                            label="Lead preso"
                            color={props.contact?.outcome === ContactStateEnum.LEADTAKEN ? "green" : 'gray'}
                            icon={<CheckCircleIcon color={""} size={0} />}
                            onClick={() => { if (props.contact?.outcome !== ContactStateEnum.LEADTAKEN) setConfirmLeadTaken(true) }}
                        />
                    </div>
                </div>
            }
            <EditPersonForm
                setIsEmpty={setIsEdited}
                isInTargetList={props.isInTargetList}
                person={props.employee}
                handleClose={() => {
                    if (
                        isEdited &&
                        !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')))
                        setShowNotSavedChanges(true)
                    else
                        props.navigate('list')
                }}
            />
            <div className="h-px w-full bg-neutral-200" />
            <div className="p-4 w-full flex flex-col items-end justify-end gap-2">
                <div className="flex items-center gap-2">
                    <Button fullWidth disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')} size={"sm"} onClick={() => setOpenDissociateEmployeeModal(true)} iconPosition={"left"} label="Dissocia da azienda" variant={"outline"} color={"red"} icon={<MinusCircleIcon color={"red"} size={16} />} />
                    <Button fullWidth disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')} size={"sm"} onClick={() => setDeleteEmployeeModalOpen(true)} iconPosition={"left"} label="Elimina dipendente" variant={"solid"} color={"red"} icon={<TrashIcon color={"red"} size={16} />} />
                </div>
            </div>
            <ConfirmModal
                visible={deleteEmployeeModalOpen}
                onClose={() => setDeleteEmployeeModalOpen(false)}
                title="Elimina dipendente"
                loading={isDeletingEmployee}
                label="Sei sicuro di voler eliminare questo dipendente? L'operazione è irreversibile."
                confirmAction={() => handleDeleteEmployee()}
            />
            <DissociateEmployeeModal
                isOpen={openDissociateEmployeeModal}
                onClose={() => setOpenDissociateEmployeeModal(false)}
                navigate={props.navigate}
                person={props.employee!}
                setOpenAssociateModal={setOpenAssociateModal}
            />
            <AssociateCompanyModal
                visible={openAssociateModal}
                onClose={() => {
                    setOpenAssociateModal(false)
                    props.navigate('list')
                }}
                person={props.employee!}
                operation="dissociate"
            />
            <LeadTakenModal
                person={props.employee!}
                isLoading={isUpdatingContactState}
                handleConfirm={handleUpdateContactState}
                listIndex={-1}
                isOpen={confirmLeadTaken}
                onClose={function (): void {
                    setConfirmLeadTaken(false);
                }}
            />
            <NotSavedChangesModal
                visible={showNotSavedChanges}
                onClose={() => setShowNotSavedChanges(false)}
                saveAction={() => {
                    setShowNotSavedChanges(false)
                }}
                goBackAction={() => {
                    props.navigate('list')
                    setShowNotSavedChanges(false)
                }}
                loading={false}
            />
        </div>
    )
}
